import type { EasyModePlaceSelection } from "@/types/easyModePlace";

export type PlaceHierarchyLabels = {
  /** Bold primary line */
  primary: string;
  /** Grey secondary line (may be empty) */
  secondary: string;
};

function normalizeLabel(value: string | null | undefined): string {
  return value?.trim() ?? "";
}

function sameIgnoreCase(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Display hierarchy for search summary:
 * - Default: locality (area) / city
 * - If locality === city: city / state
 */
export function getPlaceHierarchyLabels(
  place: EasyModePlaceSelection
): PlaceHierarchyLabels {
  const locality = normalizeLabel(place.area) || normalizeLabel(place.locality);
  const city = normalizeLabel(place.city);
  const state = normalizeLabel(place.state);

  if (locality && city && sameIgnoreCase(locality, city)) {
    return {
      primary: city,
      secondary: state,
    };
  }

  if (locality && city) {
    return { primary: locality, secondary: city };
  }

  if (city) {
    return { primary: city, secondary: state };
  }

  if (locality) {
    return { primary: locality, secondary: state };
  }

  return {
    primary: normalizeLabel(place.displayName) || normalizeLabel(place.name) || "—",
    secondary: state,
  };
}

/** Format ISO date as `AUG 8, 2026` for the summary footer. */
export function formatSearchSummaryDate(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  const date = new Date(y, m - 1, d);
  return date
    .toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    .toUpperCase();
}

export function formatSearchSummarySeats(seats: number): string {
  const count = Math.max(1, Math.floor(seats) || 1);
  return `${count} seat${count === 1 ? "" : "s"}`;
}
